import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useOrderNotesQuery,
  useAddNote,
  useOrderHistoryQuery,
  useChangeStatus,
  orderKeys,
  useOrderStatusesQuery,
  useOrderQuery,
} from "@queries/order";
import api from "@services/api";
import "./css/OrderTabs.css";
import { toLocalDateTime } from "@lib/datetime";

const CANCELLATION_REASONS = [
  "Capacity Issue",
  "The request was declined due to the inability to clean.",
  "Will Order Again Soon",
  "Made Another Order",
  "Called but no one answered, Sent a WhatsApp message and Canceled",
  "Test order by customer",
  "Price Issue",
  "Logistics issue - internal coordination issue",
  "Collection Order",
  "Need to change the payment method",
  "Logistics issue - unsuitable timeslots",
  "Customer service issue",
  "Test order - Tech department",
];

const CLEANING_STATUS_ID = 17;
const COMPLETE_STATUS_ID = 5;

// choose ONE way to detect cancel
const isCancelStatus = (statusText, statusId) => {
  // Option A (preferred if you have stable IDs): return Number(statusId) === YOUR_CANCEL_STATUS_ID;
  // Option B (works without IDs): detect by text
  return String(statusText || "").toLowerCase().includes("cancel");
};

// Collect-balance checkpoint: before Cleaning OR before Complete (both are
// points where the real total is known and the card should be reconciled).
const isCleaningStatus = (statusText, statusId) => {
  const normalized = String(statusText || "").trim().toLowerCase();
  return (
    Number(statusId) === CLEANING_STATUS_ID ||
    Number(statusId) === COMPLETE_STATUS_ID ||
    normalized === "cleaning" ||
    normalized === "complete" ||
    normalized === "completed"
  );
};

const isCreditCardPayment = (paymentMethod) => {
  const normalized = String(paymentMethod || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  // ONLY online card payments (PayFort / Credit Card) — these have money on a
  // saved card that a balance can be collected from. Everything else (cash,
  // CliQ, bank transfer, and card-on-delivery, which is collected in person) is
  // offline, so no remaining-balance dialog is shown.
  return (
    normalized === "cc" ||
    normalized === "credit card" ||
    normalized === "payfort"
  );
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;


function useCleaningPaymentFlow(orderId, paymentMethod) {
  const changeStatus = useChangeStatus(orderId);
  const qc = useQueryClient();
  const [feedback, setFeedback] = React.useState(null);
  const [dialog, setDialog] = React.useState({ open: false, request: null, statusName: "" });
  const [busy, setBusy] = React.useState(false);

  const isWorking = changeStatus.isPending || changeStatus.isLoading || busy;

  const requestStatusChange = React.useCallback(
    ({ status, statusName, comment = "", notify = true, override = false }) => {
      if (!status) return;
      const request = { status: Number(status), comment, notify, override };
      setFeedback(null);

      // The remaining card balance is now collected AUTOMATICALLY on the server
      // when the order reaches Cleaning (for any status-change path), so the UI
      // no longer charges here — that avoids a double-charge race. Just change the
      // status; the backend charges the saved card and logs it to the order.
      changeStatus.mutate(request, {
        onSuccess: () => {
          if (isCreditCardPayment(paymentMethod) && isCleaningStatus(statusName, status)) {
            setFeedback({ type: "success", text: `Order moved to ${statusName || "Cleaning"}. The remaining balance is being charged to the card automatically.` });
          }
        },
      });
    },
    [changeStatus, paymentMethod]
  );

  const closeDialog = React.useCallback(() => {
    if (isWorking) return;
    setDialog({ open: false, request: null, statusName: "" });
  }, [isWorking]);

  const confirmDialog = React.useCallback(async () => {
    if (!dialog.request || isWorking) return;
    const statusName = dialog.statusName || "Cleaning";
    setFeedback(null);
    setBusy(true);

    try {
      await changeStatus.mutateAsync(dialog.request);
    } catch (error) {
      setBusy(false);
      setDialog({ open: false, request: null, statusName: "" });
      setFeedback({ type: "warn", text: `Failed to change order status: ${getErrorMessage(error, "status_change_failed")}` });
      return;
    }

    // Charge ONLY the remaining balance (order total minus whatever was already
    // taken — e.g. the 3 JOD the app collected). If nothing was paid yet, that's
    // the full total; if already fully paid, nothing is charged.
    let text = `Order moved to ${statusName}.`;
    let type = "success";
    try {
      const { data } = await api.post(`/washy/order/${orderId}/charge-remaining`);
      if (data?.status === "success") text = `Order moved to ${statusName} and ${data.charged} JOD was charged to the card.`;
      else if (data?.status === "nothing_due") text = `Order moved to ${statusName}. The card was already fully paid.`;
      else if (data?.status === "already_charged") text = `Order moved to ${statusName}. The balance was already charged earlier — nothing charged again.`;
      else if (data?.status === "needs_manual") { text = `Order moved to ${statusName}. Couldn’t read the earlier payment — please collect any balance manually.`; type = "warn"; }
      else { text = `Order moved to ${statusName}, but the card charge failed: ${data?.message || "error"}.`; type = "warn"; }
    } catch (error) {
      text = `Order moved to ${statusName}, but the card charge failed: ${getErrorMessage(error, "payment_failed")}.`;
      type = "warn";
    }

    qc.invalidateQueries({ queryKey: orderKeys.one(orderId) });
    qc.invalidateQueries({ queryKey: orderKeys.history(orderId) });
    qc.invalidateQueries({ queryKey: ["orders"] });

    setBusy(false);
    setDialog({ open: false, request: null, statusName: "" });
    setFeedback({ type, text });
  }, [changeStatus, dialog.request, dialog.statusName, isWorking, orderId, qc]);

  return { dialog, feedback, isWorking, requestStatusChange, closeDialog, confirmDialog };
}


export default function OrderTabs({ orderId }) {
  const [tab, setTab] = React.useState("notes");

  return (
    <div className="order-tabs-wrap">
      <div className="order-card">
        <div className="order-header">
          <div className="order-tabbar">
            <button
              className={`tab-btn ${tab === "notes" ? "active" : ""}`}
              onClick={() => setTab("notes")}
            >
              Notes
            </button>
            <button
              className={`tab-btn ${tab === "history" ? "active" : ""}`}
              onClick={() => setTab("history")}
            >
              History
            </button>
          </div>
        </div>

        <div className="order-body">
          {tab === "history" ? (
            <HistoryTab orderId={orderId} />
          ) : (
            <NotesTab orderId={orderId} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== NOTES TAB ===================== */
function NotesTab({ orderId }) {
  const qc = useQueryClient();
  const { data: serverNotes = [], isLoading } = useOrderNotesQuery(orderId);
  const addNote = useAddNote(orderId);

  const [comment, setComment] = React.useState("");
  const [notes, setNotes] = React.useState([]);

  React.useEffect(() => {
    setNotes(Array.isArray(serverNotes) ? serverNotes : []);
  }, [serverNotes]);

  const handleAdd = () => {
    const text = comment.trim();
    if (!text) return;

    const optimistic = {
      date_added: new Date().toISOString().replace("T", " ").slice(0, 19),
      comment: text,
      edited_by: "you",
      _optimistic: true,
    };

    setNotes((n) => [optimistic, ...n]);

    addNote.mutate(
      { comment: text },
      {
        onSuccess: () => {
          setComment("");
          qc.invalidateQueries({ queryKey: orderKeys.notes(orderId) });
        },
        onError: () => {
          setNotes((n) => n.filter((x) => x !== optimistic));
        },
      }
    );
  };

  return (
    <>
      <section className="order-section">
        <div className="table-wrap">
          <table className="notes-table">
            <thead>
              <tr>
                <th style={{ width: "220px" }}>Date Added</th>
                <th>Comment</th>
                <th style={{ width: "180px", textAlign: "right" }}>Added By</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="muted">Loading…</td>
                </tr>
              ) : notes.length ? (
                notes.map((n, i) => (
                  <tr key={i} className={`notes-row ${n._optimistic ? "optimistic" : ""}`}>
                    <td>{n.date_added}</td>
                    <td>{n.comment}</td>
                    <td style={{ textAlign: "right" }}>{n.edited_by}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted">No notes</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="order-section">
        <label className="order-label">Add note</label>
        <textarea
          rows={4}
          className="order-textarea"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Write a note for this order…"
        />
        <div className="order-actions">
          <button
            className="btn-primary"
            onClick={handleAdd}
            disabled={!comment.trim() || addNote.isLoading}
          >
            Add Order Note
          </button>
        </div>
      </section>
    </>
  );
}

/* ===================== HISTORY TAB ===================== */
/* Includes:
   1) Current → Next with one-click "Change to Next"
   2) Manual dropdown to change to any status
   3) Full history table
*/
function HistoryTab({ orderId }) {
  const { data: history = [], isLoading } =
    useOrderHistoryQuery?.(orderId) ?? { data: [], isLoading: false };
  const { data: order } = useOrderQuery(orderId);
  const paymentFlow = useCleaningPaymentFlow(orderId, order?.payment_method);

  const latest = Array.isArray(history) && history.length ? history[0] : null;
  const currentStatusId = order?.order_status_id ?? latest?.order_status_id ?? "";

  return (
    <>
      <HistoryTable history={history} isLoading={isLoading} />

      {paymentFlow.feedback ? (
        <section className="order-section">
          <div className={`oc-alert ${paymentFlow.feedback.type}`}>
            {paymentFlow.feedback.text}
          </div>
        </section>
      ) : null}

      {/* <NotesTab orderId={orderId} />*/}
      <AdvanceToNext
        latest={latest}
        loading={isLoading}
        onSubmitStatus={paymentFlow.requestStatusChange}
        isWorking={paymentFlow.isWorking}
        isBlocked={paymentFlow.isWorking || paymentFlow.dialog.open}
      />
      <ManualChange
        currentStatusId={currentStatusId}
        orderId={orderId}
        onSubmitStatus={paymentFlow.requestStatusChange}
        isWorking={paymentFlow.isWorking}
        isBlocked={paymentFlow.isWorking || paymentFlow.dialog.open}
      />
      <PaymentConfirmDialog
        open={paymentFlow.dialog.open}
        statusName={paymentFlow.dialog.statusName}
        isWorking={paymentFlow.isWorking}
        onCancel={paymentFlow.closeDialog}
        onConfirm={paymentFlow.confirmDialog}
      />
    </>
  );
}

/* ---- 1) Current → Next with one-click change ---- */
function AdvanceToNext({ latest, loading, onSubmitStatus, isWorking, isBlocked }) {
  const [notify, setNotify] = React.useState(true);
  const [cooldown, setCooldown] = React.useState(0);
  const wasWorkingRef = React.useRef(false);

  React.useEffect(() => {
    let t;
    if (cooldown > 0) {
      t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(t);
  }, [cooldown]);

  React.useEffect(() => {
    if (isWorking && !wasWorkingRef.current) {
      setCooldown(5);
    }

    wasWorkingRef.current = isWorking;
  }, [isWorking]);

  const onAdvance = () => {
    if (!latest?.next_order_status_id) return;
    onSubmitStatus({
      status: latest.next_order_status_id,
      statusName: latest.next_status_text,
      comment: "",
      notify,
      override: false,
    });
  };

  return (
    <section className="order-section">
      <div className="order-label">Status</div>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : latest ? (
        <>
          <div className="status-row">
            <span className="status-badge">{latest.status_text}</span>
            <span className="status-arrow">→</span>
            <span className="status-badge next">{latest.next_status_text || "-"}</span>

            <div className="order-actions">
              <button
                className="btn-primary"
                onClick={onAdvance}
                disabled={!latest.next_order_status_id || isBlocked || cooldown > 0}
                title={
                  latest.next_order_status_id
                    ? `Change to ${latest.next_status_text}`
                    : "No next status"
                }
              >
                {isWorking ? "Working..." : cooldown > 0 ? `Wait ${cooldown}s…` : `Change to ${latest.next_status_text || "-"}`}
              </button>
            </div>
          </div>

          <div className="form-grid" style={{ marginTop: 10 }}>
            <label className="order-checkbox">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                disabled={isBlocked}
              />
              Notify customer
            </label>
          </div>
        </>
      ) : (
        <div className="muted">No history</div>
      )}
    </section>
  );
}


/* ---- 2) Manual dropdown to set any status ---- */
function ManualChange({ currentStatusId, onSubmitStatus, isWorking, isBlocked }) {
  const { data: statuses = [], isLoading } = useOrderStatusesQuery();

  const [status, setStatus] = React.useState("");
  const [notify, setNotify] = React.useState(true);
  const [comment, setComment] = React.useState("");
  const [cancelReason, setCancelReason] = React.useState("");
  const [cooldown, setCooldown] = React.useState(0);
  const wasWorkingRef = React.useRef(false);

  const selectedStatusObj = statuses.find((s) => String(s.order_status_id) === String(status));
  const selectedIsCancel = isCancelStatus(selectedStatusObj?.name, status);

  React.useEffect(() => {
    setStatus(currentStatusId ? String(currentStatusId) : "");
  }, [currentStatusId]);

  React.useEffect(() => {
    let t;
    if (cooldown > 0) {
      t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(t);
  }, [cooldown]);

  React.useEffect(() => {
    if (isWorking && !wasWorkingRef.current) {
      setCooldown(5);
    }

    wasWorkingRef.current = isWorking;
  }, [isWorking]);

  React.useEffect(() => {
    if (!selectedIsCancel) setCancelReason("");
  }, [selectedIsCancel]);

  const submit = () => {
    if (!status) return;
    if (selectedIsCancel && !cancelReason) return;

    const finalComment = selectedIsCancel
      ? `Cancellation reason: ${cancelReason}${comment ? " | " + comment : ""}`
      : comment;

    onSubmitStatus({
      status: Number(status),
      statusName: selectedStatusObj?.name,
      notify,
      override: false,
      // SEND REASON INSIDE COMMENT
      comment: finalComment,
    });
  };

  return (
    <section className="order-section">
      <div className="order-label">Change status</div>

      <div className="form-grid">
        <select
          className="order-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={isLoading || isBlocked}
        >
          <option value="">Select status…</option>
          {statuses.map((s) => (
            <option key={s.order_status_id} value={s.order_status_id}>
              {s.name}
            </option>
          ))}
        </select>

        <label className="order-checkbox">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            disabled={isBlocked}
          />
          Notify customer
        </label>

        {selectedIsCancel ? (
          <select
            className="order-select"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            disabled={isBlocked}
          >
            <option value="">Select Cancellation Reason</option>
            {CANCELLATION_REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        ) : null}
      </div>

      <textarea
        rows={3}
        className="order-textarea"
        placeholder="Optional comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        disabled={isBlocked}
        style={{ marginTop: 10 }}
      />

      <div className="order-actions">
        <button
          className="btn-primary"
          onClick={submit}
          disabled={!status || isBlocked || (selectedIsCancel && !cancelReason) || cooldown > 0}
        >
          {isWorking ? "Working..." : cooldown > 0 ? `Wait ${cooldown}s…` : "Change status"}
        </button>
      </div>
    </section>
  );
}

function PaymentConfirmDialog({ open, statusName, isWorking, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="oc-modal-backdrop" role="presentation">
      <div className="oc-modal" role="dialog" aria-modal="true" aria-labelledby="confirm-payment-title">
        <div className="oc-modal-head">
          <strong id="confirm-payment-title">Confirm Payment</strong>
          <button type="button" className="btn btn-default btn-sm" onClick={onCancel} disabled={isWorking}>
            Close
          </button>
        </div>

        <div className="oc-modal-body">
          <p style={{ margin: 0 }}>
            This order uses credit card payment. If you continue, the order status will be changed to {statusName || "Cleaning"} and the system will charge any remaining amount to the saved card.
          </p>
        </div>

        <div className="oc-modal-foot">
          <button type="button" className="btn btn-default" onClick={onCancel} disabled={isWorking}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={isWorking}>
            {isWorking ? "Processing..." : "Confirm and Charge"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---- 3) Table ---- */
function HistoryTable({ history, isLoading }) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(history.length / itemsPerPage);
  const paginatedHistory = history.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <section className="order-section">
      <div className="table-wrap">
        <table className="notes-table">
          <thead>
            <tr>
              <th style={{ width: "200px" }}>Date Added</th>
              <th>Comment</th>
              <th style={{ width: "160px" }}>Status</th>
              <th style={{ width: "160px" }}>Customer Notified</th>
              <th style={{ width: "180px", textAlign: "right" }}>Edited By</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="muted">Loading…</td>
              </tr>
            ) : paginatedHistory.length ? (
              paginatedHistory.map((h) => (
                <tr key={h.order_history_id}>
                  <td>{toLocalDateTime(h.date_added)}</td>
                  <td>{h.comment || "-"}</td>
                  <td>{h.status_text || "-"}</td>
                  <td>{h.notify ? "Yes" : "No"}</td>
                  <td style={{ textAlign: "right" }}>{h.edited_by || "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="muted">No history</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', gap: '5px' }}>
          <button
            className="btn btn-default btn-sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <button
              key={pageNum}
              className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-default'}`}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum}
            </button>
          ))}

          <button
            className="btn btn-default btn-sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

